/**
 * useOrbDocCoreConceptsTraitsENDocEx2DialLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait OrbDocCoreConceptsTraitsENDocEx2Dial
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, runTickFrame, useEventBus, useUIEvents } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import type { EntityFieldWrite, EntityFrameState, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { EventResponse, OrbDocCoreConceptsTraitsENThermostat, OrbDocCoreConceptsTraitsENDocEx2DialEventPayloadMap } from '@app/shared';

export interface OrbDocCoreConceptsTraitsENDocEx2DialConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

type OrbDocCoreConceptsTraitsENDocEx2DialSlot = 'main';
export interface OrbDocCoreConceptsTraitsENDocEx2DialLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<OrbDocCoreConceptsTraitsENDocEx2DialSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocCoreConceptsTraitsENThermostat>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocCoreConceptsTraitsENDocEx2DialEventPayloadMap]: (payload?: OrbDocCoreConceptsTraitsENDocEx2DialEventPayloadMap[K]) => Promise<EventResponse> };
  config?: OrbDocCoreConceptsTraitsENDocEx2DialConfig;
}

export function useOrbDocCoreConceptsTraitsENDocEx2DialLogic(config?: Partial<OrbDocCoreConceptsTraitsENDocEx2DialConfig>): OrbDocCoreConceptsTraitsENDocEx2DialLogicReturn {
  const mergedConfig = config;

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'OrbDocCoreConceptsTraitsENThermostat';
  _sharedStore.seed(_sharedEntityId, { 'target': 21 });
  const _sharedTickEmitsRef = useRef<Array<() => void>>([]);
  const _sharedWritersRef = useRef<ReadonlyArray<(scratch: EntityFrameState) => readonly EntityFieldWrite[]>>([
  ]);

  // Tick: OrbDocCoreConceptsTraitsENThermostatSharedTick (every frame)
  const orbDocCoreConceptsTraitsENThermostatSharedTickRef = useRef<number>(0);
  useEffect(() => {
    let running = true;
    const tick = () => {
      if (!running) return;
      if (true) {
        runTickFrame(_sharedEntityId, _sharedWritersRef.current, _sharedStore);
        for (const _emit of _sharedTickEmitsRef.current.splice(0)) _emit();
      }
      orbDocCoreConceptsTraitsENThermostatSharedTickRef.current = requestAnimationFrame(tick);
    };
    orbDocCoreConceptsTraitsENThermostatSharedTickRef.current = requestAnimationFrame(tick);
    return () => {
      running = false;
      cancelAnimationFrame(orbDocCoreConceptsTraitsENThermostatSharedTickRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<OrbDocCoreConceptsTraitsENThermostat>;
  const data: Record<string, EntityRow[]> = { 'OrbDocCoreConceptsTraitsENThermostat': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<OrbDocCoreConceptsTraitsENDocEx2DialSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocCoreConceptsTraitsENThermostat'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'UP' && ((+(fields?.target ?? 0) < 28))) {
      // idle → idle (on UP)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocCoreConceptsTraitsENDocEx2Dial' }); emitCascadeEvent(eventBus, 'OrbDocCoreConceptsTraitsEN', 'OrbDocCoreConceptsTraitsENDocEx2Dial', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocCoreConceptsTraitsEN', trait: 'OrbDocCoreConceptsTraitsENDocEx2Dial' } }); })({ event: "UP" as const }));
        _fields['target'] = (+(fields?.target ?? 0) + 1);
        fields = { ...(fields ?? {}), 'target': _fields['target'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('TARGET_CHANGED', _p, { trait: 'OrbDocCoreConceptsTraitsENDocEx2Dial' }); emitCascadeEvent(eventBus, 'OrbDocCoreConceptsTraitsEN', 'OrbDocCoreConceptsTraitsENDocEx2Dial', { event: 'TARGET_CHANGED', payload: _p, source: { orbital: 'OrbDocCoreConceptsTraitsEN', trait: 'OrbDocCoreConceptsTraitsENDocEx2Dial' } }); })({ target: fields?.target }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'DOWN' && ((+(fields?.target ?? 0) > 16))) {
      // idle → idle (on DOWN)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocCoreConceptsTraitsENDocEx2Dial' }); emitCascadeEvent(eventBus, 'OrbDocCoreConceptsTraitsEN', 'OrbDocCoreConceptsTraitsENDocEx2Dial', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocCoreConceptsTraitsEN', trait: 'OrbDocCoreConceptsTraitsENDocEx2Dial' } }); })({ event: "DOWN" as const }));
        _fields['target'] = (+(fields?.target ?? 0) - 1);
        fields = { ...(fields ?? {}), 'target': _fields['target'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('TARGET_CHANGED', _p, { trait: 'OrbDocCoreConceptsTraitsENDocEx2Dial' }); emitCascadeEvent(eventBus, 'OrbDocCoreConceptsTraitsEN', 'OrbDocCoreConceptsTraitsENDocEx2Dial', { event: 'TARGET_CHANGED', payload: _p, source: { orbital: 'OrbDocCoreConceptsTraitsEN', trait: 'OrbDocCoreConceptsTraitsENDocEx2Dial' } }); })({ target: fields?.target }));
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocCoreConceptsTraitsENDocEx2DialSlot[]; clear: OrbDocCoreConceptsTraitsENDocEx2DialSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocCoreConceptsTraitsENDocEx2DialSlot[], clear: [] as OrbDocCoreConceptsTraitsENDocEx2DialSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'OrbDocCoreConceptsTraitsENThermostat': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'OrbDocCoreConceptsTraitsENDocEx2Dial': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocCoreConceptsTraitsENDocEx2DialEventPayloadMap]: (payload?: OrbDocCoreConceptsTraitsENDocEx2DialEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'UP': () => dispatch('UP'),
    'DOWN': () => dispatch('DOWN'),
    'TARGET_CHANGED': (payload?: OrbDocCoreConceptsTraitsENDocEx2DialEventPayloadMap['TARGET_CHANGED']) => dispatch('TARGET_CHANGED', payload),
    'MOVED': (payload?: OrbDocCoreConceptsTraitsENDocEx2DialEventPayloadMap['MOVED']) => dispatch('MOVED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocCoreConceptsTraitsEN.OrbDocCoreConceptsTraitsENDocEx2Dial', ['INIT', 'UP', 'DOWN'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
